import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Keycloak from 'keycloak-js';
import type { AuthUser } from '@chefty/shared-types';
import { api } from '../services/api';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRole: (role: string) => boolean;
  login: () => void;
  logout: () => void;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'master',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'admin-client',
};

const keycloak = new Keycloak(keycloakConfig);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    keycloak
      .init({ onLoad: 'check-sso', silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html' })
      .then((authenticated) => {
        if (authenticated && keycloak.tokenParsed) {
          setUser({
            // Client-side identity from the token. The internal `users.id` is
            // server-resolved (ticket 1.1) and only known after GET /users/me —
            // adopting it here is the client-auth ticket's job; until then `id`
            // mirrors the Keycloak sub for display/role-gating only.
            id: keycloak.tokenParsed.sub || '',
            keycloakId: keycloak.tokenParsed.sub || '',
            email: keycloak.tokenParsed.email || '',
            roles: keycloak.tokenParsed.realm_access?.roles || [],
            // Web admin always signs in through Keycloak — never an anon identity.
            anonymous: false,
          });
          // Attach the bearer to the API client BEFORE children mount, so the
          // first admin fetch (in a child effect, which runs before this one's
          // parent effect) already carries the token instead of 401-ing.
          api.setToken(keycloak.token || null);
          setToken(keycloak.token || null);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Keycloak init error:', error);
        setIsLoading(false);
      });

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).then((refreshed) => {
        if (refreshed) {
          api.setToken(keycloak.token || null);
          setToken(keycloak.token || null);
        }
      });
    };
  }, []);

  const login = useCallback(() => {
    keycloak.login();
  }, []);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const hasRole = useCallback((role: string) => {
    return user?.roles.includes(role) || false;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        hasRole,
        login,
        logout,
        token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
